<script setup>
// Search for a GIF and hand its bytes back. Everything goes through the app's
// own origin — /api/gif/search and /api/gif/bytes — so the provider's key never
// reaches this file and the CSP stays `'self'`.
//
// ATTRIBUTION IS NOT OPTIONAL. GIPHY's terms: "We require all apps that use the
// GIPHY API to conspicuously display 'Powered By GIPHY' attribution marks where
// the API is utilized." KLIPY's terms ask the same of "Powered by KLIPY". The
// server answers from whichever provider it has a key for and says which in
// the response, so the mark follows the provider of the last search that
// answered. Before anything has answered it names both — it is never blank.
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { IonModal } from '@ionic/vue';
import { say } from '../fax/voice.js';
import { debounce } from '../core/util.js';
// ON THE PHONE THE APP IS NOT SERVED BY THE SERVER. It loads from
// capacitor://localhost, so a bare `/api/...` never leaves the device. The OTA
// client has always prefixed API_BASE for this reason; the picker did not, the
// probe failed, and the tray hid itself exactly as designed — on every phone.
import { API_BASE } from '../core/native.js';

const emit = defineEmits(['pick', 'close']);
const open = ref(true);
let pending = null;            // what to emit once Ionic has finished dismissing
const close = () => { open.value = false; };
function afterDismiss() { const p = pending; pending = null; if (p) emit('pick', ...p); else emit('close'); }

const q = ref('');
const results = ref([]);
const busy = ref(false);
const fetching = ref(null);   // id of the GIF whose bytes are downloading
const status = ref('');       // one line, in the LCD
const off = ref(false);       // server has no provider configured
const provider = ref('');     // 'klipy' | 'giphy' — who answered the last search
// KLIPY's attribution terms REQUIRE "Search KLIPY" in the search box's
// placeholder (docs.klipy.com/attribution). It names whoever is answering;
// KLIPY is the primary, so it is the assumption until GIPHY answers instead.
const hint = computed(() => (provider.value === 'giphy'
  ? say('SEARCH GIPHY', 'Search GIPHY')
  : say('SEARCH KLIPY', 'Search KLIPY')));
const mark = computed(() => {
  if (provider.value === 'klipy') return 'POWERED BY KLIPY';
  if (provider.value === 'giphy') return 'POWERED BY GIPHY';
  return 'GIFS BY KLIPY AND GIPHY';
});
const inputEl = ref(null);

onMounted(async () => {
  await nextTick();
  inputEl.value?.focus();
});

const LIMIT = say('GIF SEARCH IS OVER ITS HOURLY LIMIT. TRY AGAIN LATER.', 'GIF search is over its hourly limit. Try again later.');

async function search() {
  const term = q.value.trim();
  if (!term) { results.value = []; status.value = ''; return; }
  busy.value = true;
  status.value = say('SEARCHING…', 'Searching…');
  try {
    const r = await fetch(`${API_BASE}/api/gif/search?q=${encodeURIComponent(term)}&limit=24`);
    if (r.status === 503) { off.value = true; status.value = ''; return; }
    // Over GIPHY's hourly cap. Not a fault, not "the line is down": the
    // machine says when to come back, and the rest of the sheet still works.
    if (r.status === 429) { results.value = []; status.value = LIMIT; return; }
    if (!r.ok) throw new Error(String(r.status));
    const body = await r.json();
    if (body.provider === 'klipy' || body.provider === 'giphy') provider.value = body.provider;
    results.value = body.results || [];
    status.value = results.value.length
      ? say(`${results.value.length} FOUND`, `${results.value.length} found`)
      : say('NOTHING FOUND. TRY ANOTHER WORD.', 'Nothing found. Try another word.');
  } catch {
    status.value = say('THE LINE IS DOWN. TRY AGAIN.', "The line's down. Try again.");
  } finally {
    busy.value = false;
  }
}
const searchSoon = debounce(search, 350);
watch(q, () => searchSoon());

async function choose(g) {
  if (fetching.value) return;
  fetching.value = g.id;
  status.value = say('LOADING ORIGINAL…', 'Loading original…');
  try {
    const r = await fetch(`${API_BASE}/api/gif/bytes?id=${encodeURIComponent(g.id)}`);
    if (r.status === 413) { status.value = say('THAT ONE IS TOO BIG FOR THE MACHINE.', "That one's too big for the machine."); return; }
    if (r.status === 429) { status.value = LIMIT; return; }
    if (!r.ok) throw new Error(String(r.status));
    const blob = await r.blob();
    pending = [blob, g];
    close();
  } catch {
    status.value = say('COULD NOT LOAD THAT ONE.', "Couldn't load that one.");
  } finally {
    fetching.value = null;
  }
}
</script>

<template>
  <IonModal :is-open="open" :breakpoints="[0, 0.94]" :initial-breakpoint="0.94" :handle="false" class="gif-sheet" :aria-label="say('FIND A GIF', 'Find a GIF')" @didDismiss="afterDismiss">
    <div class="modal-panel">
      <div class="head">
        <span class="silkscreen">{{ say('FIND A GIF', 'Find a GIF') }}</span>
        <button type="button" class="closer" @click="close">{{ say('CLOSE', 'Close') }}</button>
      </div>

      <label class="lcd-wrap">
        <span class="vh">{{ say('SEARCH', 'Search') }}</span>
        <input
          ref="inputEl"
          v-model="q"
          class="lcd"
          type="search"
          autocomplete="off"
          autocapitalize="off"
          spellcheck="false"
          :placeholder="hint"
          @keydown.enter.prevent="search"
        >
      </label>

      <p class="status silkscreen" aria-live="polite">{{ status }}</p>

      <p v-if="off" class="plain-note">
        {{ say('NO GIF PROVIDER IS CONFIGURED ON THIS MACHINE.', 'No GIF provider is configured on this machine.') }}
      </p>

      <ul v-else class="grid" :aria-busy="busy">
        <li v-for="g in results" :key="g.id">
          <button
            type="button"
            class="cell"
            :disabled="!!fetching"
            :aria-label="g.title || say('A GIF', 'A GIF')"
            @click="choose(g)"
          >
            <img
              :src="`${API_BASE}/api/gif/bytes?id=${encodeURIComponent(g.id)}&size=preview`"
              :alt="''"
              loading="lazy"
              decoding="async"
            >
            <span v-if="fetching === g.id" class="loading silkscreen">{{ say('LOADING', 'Loading') }}</span>
          </button>
        </li>
      </ul>

      <!-- The mark. Conspicuous, permanent, and it follows whoever answered. -->
      <p class="powered silkscreen">{{ mark }}</p>
    </div>
  </IonModal>
</template>

<style scoped>
/* NOT .panel: that is the machine's control panel in app.css, and its
   flex-wrap: wrap leaked in here. A wrapping column with the keyboard up put
   the whole GIF grid in a second column off the right edge of the phone. */
.modal-panel {
  flex-wrap: nowrap; align-items: stretch;
  width: 100%; height: 100%; max-width: none; max-height: none;
  display: flex; flex-direction: column; gap: 10px;
  padding: 12px 14px calc(14px + env(safe-area-inset-bottom));
  background: var(--machine);
}
.head { display: flex; align-items: center; justify-content: space-between; }
.lcd-wrap { display: block; }
.lcd { width: 100%; min-height: 52px; padding: 0 14px; font-size: 1.1rem; }
.status { min-height: 1.2em; margin: 0; }
.grid {
  list-style: none; margin: 0; padding: 0;
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
  overflow-y: auto; min-height: 120px;
}
.cell {
  position: relative; display: block; width: 100%; aspect-ratio: 1;
  padding: 0; border: 2px solid var(--machine-dark); border-radius: 6px;
  background: var(--paper, #fff); overflow: hidden; cursor: pointer;
}
.cell img { width: 100%; height: 100%; object-fit: cover; display: block; }
.cell:focus-visible { outline: 3px solid var(--led-red); outline-offset: 2px; }
.loading {
  position: absolute; inset: auto 0 0 0; padding: 4px;
  background: rgba(0, 0, 0, 0.7); color: #fff; text-align: center;
}
.powered { margin: 4px 0 0; text-align: center; }
.vh { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
/* THE SHEET IS AN IonModal, NOT A position: fixed PANEL. An Ionic page uses
   transform and contain, which makes `fixed` mean "relative to the page" — so
   when the keyboard rose on a phone the panel was shoved offscreen and the
   grid collapsed to a single column at the right edge. Ionic presents a modal
   at the app root and handles the keyboard itself. */
:global(ion-modal.gif-sheet) { --background: var(--machine); --border-radius: 14px 14px 0 0; }
/* .dial is the CONDITION dial's container; on a lone button it styled nothing. */
.closer {
  min-height: var(--tap); padding: 0 14px;
  border: 2px solid var(--machine-dark); border-radius: var(--radius-sm);
  background: var(--machine-light); color: var(--legend);
  font-family: var(--mono); font-size: var(--text-xs); font-weight: 800;
  letter-spacing: var(--tracking-wide); text-transform: uppercase; cursor: pointer;
}
</style>